
import { useCallback, useEffect, useRef, useState } from 'react'
import { StreamChat } from 'stream-chat'
import { setUnreadCount, useUnreadCount } from '../stores'

type UseChatBadgeCountProps = {
  client: StreamChat
  initialCount?: number
  setAppBadge?: (count: number) => void
}

export const useChatBadgeCount = (props: UseChatBadgeCountProps) => {
  const { client, initialCount, setAppBadge } = props
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unreadCount = useUnreadCount()

  useEffect(() => {
    setUnreadCount(initialCount ?? 0)
  } ,[])
  
  async function getUnread() {
    const { total_unread_count } = await client?.getUnreadCount?.() ?? { total_unread_count: 0 }
    setUnreadCount(total_unread_count)
    return total_unread_count
  }

  const updateBadgeCount = useCallback(async (e) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(async () => {
      const unreadMessages = await getUnread()
      setAppBadge?.(unreadMessages)
    }, 300)
  }, [client])

  const forceUpdateBadgeCount = useCallback(async () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    const unreadMessages = await getUnread()
    setAppBadge?.(unreadMessages)
  }, [client])

  useEffect(() => {
    forceUpdateBadgeCount()
  }, [])

  useEffect(() => {
    if (!client) return

    client.on('message.new', updateBadgeCount)
    client.on('notification.message_new', updateBadgeCount)
    client.on('notification.mark_read', forceUpdateBadgeCount)
    client.on('notification.read', forceUpdateBadgeCount)
    console.log("Subscribed to chat events for badge count")

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      client.off('message.new', updateBadgeCount)
      client.off('notification.message_new', updateBadgeCount)
      client.off('notification.mark_read', forceUpdateBadgeCount)
      client.off('notification.read', forceUpdateBadgeCount)
    }
  }, [client])

  return {
    unreadCount,
    setUnreadCount,
    forceUpdateBadgeCount,
  }
}
